export type ISODate = `${number}-${number}-${number}`;

export type Currency = "CAD" | "USD" | "EUR";

export type InstrumentId =
  | "ICE_CANOLA"
  | "CBOT_SOYBEAN"
  | "CBOT_SOYBEAN_OIL"
  | "MATIF_RAPESEED";

export type PairPresetId =
  | "canola-soybean"
  | "canola-soybean-oil"
  | "canola-matif-rapeseed";

export type FuturesMonthCode =
  | "F"
  | "G"
  | "H"
  | "J"
  | "K"
  | "M"
  | "N"
  | "Q"
  | "U"
  | "V"
  | "X"
  | "Z";

/** The enabled Canola universe intentionally excludes September (U). */
export type EnabledCanolaMonthCode = "F" | "H" | "K" | "N" | "X";

export interface ContractRef {
  readonly instrumentId: InstrumentId;
  readonly monthCode: FuturesMonthCode;
  readonly year: number;
}

export interface ActualContractPair {
  readonly pairId: PairPresetId;
  readonly legA: ContractRef;
  readonly legB: ContractRef;
}

export const QUALITY_FLAGS = [
  "stale",
  "duplicate",
  "abnormal-jump",
  "invalid-contract-metadata",
  "settlement-misaligned",
  "low-volume",
  "low-open-interest",
  "back-adjusted",
  "source-warning",
] as const;

export type QualityFlag = (typeof QUALITY_FLAGS)[number];

export interface DatedPoint {
  readonly date: ISODate;
}

export interface DatedValue extends DatedPoint {
  readonly date: ISODate;
  readonly value: number;
}

export interface ActualEpisodeSeries<TPoint extends DatedPoint = DatedValue> {
  readonly kind: "actual-episode";
  readonly pair: ActualContractPair;
  readonly episodeId: string;
  readonly source: "actual-settlements";
  readonly backAdjusted: false;
  readonly points: readonly TPoint[];
}

export interface SeasonalEpisode<TPoint extends DatedPoint = DatedValue> {
  readonly contractYear: number;
  readonly pair: ActualContractPair;
  readonly points: readonly TPoint[];
}

export interface SeasonalPanelSeries<TPoint extends DatedPoint = DatedValue> {
  readonly kind: "seasonal-panel";
  readonly pairId: PairPresetId;
  readonly template: string;
  readonly source: "actual-contract-episodes";
  readonly backAdjusted: false;
  readonly episodes: readonly SeasonalEpisode<TPoint>[];
}

export interface QaContinuousSeries<TPoint extends DatedPoint = DatedValue> {
  readonly kind: "qa-continuous";
  readonly label: string;
  readonly source: "reference-only";
  readonly backAdjusted: boolean;
  readonly permittedUses: readonly ["qa", "reference-display"];
  readonly points: readonly TPoint[];
}

export type AnalysisSeries<TPoint extends DatedPoint = DatedValue> =
  | ActualEpisodeSeries<TPoint>
  | SeasonalPanelSeries<TPoint>
  | QaContinuousSeries<TPoint>;

export type FormalAnalysisSeries<TPoint extends DatedPoint = DatedValue> =
  | ActualEpisodeSeries<TPoint>
  | SeasonalPanelSeries<TPoint>;

export function isFormalAnalysisSeries<TPoint extends DatedPoint>(
  series: AnalysisSeries<TPoint>,
): series is FormalAnalysisSeries<TPoint> {
  return series.kind !== "qa-continuous" && series.backAdjusted === false;
}

export function requireFormalAnalysisSeries<TPoint extends DatedPoint>(
  series: AnalysisSeries<TPoint>,
): FormalAnalysisSeries<TPoint> {
  if (!isFormalAnalysisSeries(series)) {
    throw new Error(
      "QA/back-adjusted continuous series is not permitted for formal analysis or P/L.",
    );
  }

  return series;
}
